/* eslint-disable no-else-return */
import React, { useEffect, useState } from 'react';
import { FaPencilAlt } from 'react-icons/fa';
import { HiTrash } from 'react-icons/hi';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { Connection, ConnectionType } from '../../../redux/slices/connectionsSlice';
import WarningModal from './WarningModal';
import ConnectionWithIcons from '../misc/ConnectionWithIcons';
import LocalContactModal from './LocalContactModal';
import TagDisplay from '../misc/TagDisplay';
import ModalLayout from '../layouts/ModalLayout';
import SetMemberInactiveModal from './SetMemberInactiveModal';
import connectionService from '../../../services/community/connectionService';
import useSelectedCommunity from '../../../hooks/community/useSelectedCommunity';
import { getProfilePictureUrl } from '../../../utils/connectionUtils';
import ProfilePicture from '../misc/ProfilePicture';
import Button from '../buttons/Button';
import ConnectionStatus from '../misc/ConnectionStatus';
import { createEmptyCommunityInvite } from '../../../utils/formatUtils';

interface UserInfoModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  connection: Connection | undefined;
}

export default function UserInfoModal({
  open,
  setOpen,
  connection,
}: UserInfoModalProps): JSX.Element {
  const [profilePicture, setProfilePicture] = useState<string | undefined>(undefined);
  const [deleteDialog, setDeleteDialog] = useState<boolean>(false);
  const [localContactDialog, setLocalContactDialog] = useState<boolean>(false);
  const [setInactiveDialog, setSetInactiveDialog] = useState<boolean>(false);
  const [note, setNote] = useState<string>();

  const { t } = useTranslation();
  const selectedCommunity = useSelectedCommunity();

  const handleDelete = async () => {
    connectionService.deleteConnections([connection!.dashboardMemberId]);
  };

  const handleNoteSave = async () => {
    if (!connection) return;
    await connectionService.updateNote(connection.dashboardMemberId, note!);
    toast.success(t('toast.success.savedNote'));
  };

  const handleSetInactive = async (inactiveReason?: string) => {
    if (!connection || !selectedCommunity) return;

    try {
      await connectionService.updateMemberStatus(
        connection.dashboardMemberId,
        false,
        inactiveReason,
      );
      toast.success(t('toast.success.memberSetInactive'));
      setOpen(false);
    } catch (error) {
      toast.error(t('toast.error.memberSetInactiveFailed'));
    }
  };

  const handleSetActive = async () => {
    if (!connection || !selectedCommunity) return;

    try {
      await connectionService.updateMemberStatus(
        connection.dashboardMemberId,
        true,
      );
      toast.success(t('toast.success.memberSetActive'));
      setOpen(false);
    } catch (error) {
      toast.error(t('toast.error.memberSetActiveFailed'));
    }
  };

  useEffect(() => {
    if (!open) return;
    setProfilePicture(undefined);
    setNote(connection?.note!);
  }, [open]);

  useEffect(() => {
    const fetchProfilePicture = async () => {
      if (open && connection) {
        const pictureUrl = await getProfilePictureUrl(connection);
        setProfilePicture(pictureUrl);
      }
    };
    fetchProfilePicture();
  }, [open]);

  const getTypeText = () => {
    if (connection!.type === ConnectionType.MEMBER) {
      return t('contact.connection');
    } else if (connection!.type === ConnectionType.EXTERNAL_MEMBER) {
      return t('contact.addedSelf');
    }
    return '';
  };

  return (
    <>
      <ModalLayout
        open={open}
        setOpen={setOpen}
        closeButton
        className="relative z-50 w-[45rem] min-w-[300px] rounded-2xl bg-secondary-50 p-8">
        {/* Header */}
        {connection && (
          <>
            <div className="flex w-full border-b border-primary-900 pb-8">
              <div className="relative mr-6 h-fit w-28 rounded-xl">
                <ProfilePicture
                  picture={profilePicture}
                  alias={`${connection.fields.FIRST_NAME}+${connection.fields.LAST_NAME}`}
                  className="h-28 w-28 text-[40px]"
                />
                <ConnectionStatus
                  connection={connection}
                  className="absolute -bottom-3 -right-3 border border-secondary-50"
                />
              </div>

              {/* Header buttons */}
              <div className="flex flex-col">
                <h1 className="mt-1 text-3xl font-medium">{`${connection.fields.FIRST_NAME} ${connection.fields.LAST_NAME}`}</h1>
                <p className="text-gray-500">{getTypeText()}</p>
                <div className="flex">
                  {/* Edit local contact button */}
                  {connection.type === ConnectionType.EXTERNAL_MEMBER && (
                    <Button
                      variant="secondary"
                      className="mt-4"
                      onClick={() => {
                        setOpen(false);
                        setLocalContactDialog(true);
                      }}>
                      {' '}
                      {t('component.actions.connection.edit')}
                      <FaPencilAlt className="h-5 w-5" />
                    </Button>
                  )}
                </div>
              </div>
            </div>

            {/* Information only visible if there are more fields than just the name */}
            {Object.values(connection.fields).filter((x) => x).length > 1 && (
              <div className="">
                <p className="mt-6 text-xl font-medium">{t('general.information')}</p>
                <ConnectionWithIcons connection={connection} />
              </div>
            )}

            {/* Member Status Button */}
            {connection.type === ConnectionType.MEMBER && (
              <div className="mt-4">
                <Button
                  variant={connection.active ? "secondary" : "primary"}
                  onClick={() => {
                    if (connection.active) {
                      setOpen(false);
                      setSetInactiveDialog(true);
                    } else {
                      handleSetActive();
                    }
                  }}>
                  {connection.active
                    ? t('component.actions.connection.setInactive')
                    : t('component.actions.connection.setActive')
                  }
                </Button>
              </div>
            )}

            {/* Notes */}
            <div className="mt-4 flex flex-col gap-2">
              <p className="text-xl font-medium">{t('general.note')}</p>
              <textarea
                data-testid="postcard-text"
                className="mb-2 h-32 w-full resize-none rounded-lg placeholder-gray-300"
                placeholder="Schrijf hier je opmerkingen..."
                onChange={(e) => setNote(e.target.value)}
                value={note}
              />
              <Button
                variant="primary"
                onClick={handleNoteSave}
                disabled={note === connection.note || (!note && !connection.note)}>
                {t('general.save')}
              </Button>
            </div>

            {/* Tags */}
            <div className="relative flex h-fit justify-between">
              <div className="flex w-4/6 flex-col">
                <p className="mb-2 mt-6 text-xl font-medium">{t('general.tags')}</p>
                <TagDisplay connection={connection} withHover />
              </div>

              <div className="absolute bottom-0 right-0 flex flex-col gap-2">
                {/* Delete contact button */}
                <Button
                  onClick={() => {
                    setOpen(false);
                    setDeleteDialog(true);
                  }}
                  className="group px-0 underline hover:text-opacity-80">
                  {t('component.actions.connection.deleteContact')}
                  <HiTrash className="h-5 w-5" />
                </Button>
              </div>
            </div>
          </>
        )}
      </ModalLayout>
      <WarningModal
        open={deleteDialog}
        setOpen={setDeleteDialog}
        onContinue={handleDelete}
        title={
          connection?.type === ConnectionType.EXTERNAL_MEMBER
            ? t('warning.deleteLocalContact.title')
            : t('warning.deleteConnection.title')
        }
        saveClassName="bg-danger"
        description={
          connection?.type === ConnectionType.EXTERNAL_MEMBER
            ? t('warning.deleteLocalContact.description')
            : t('warning.deleteConnection.description')
        }
        button={
          connection?.type === ConnectionType.EXTERNAL_MEMBER
            ? t('warning.deleteLocalContact.title')
            : t('warning.deleteConnection.title')
        }
      />
      <LocalContactModal
        open={localContactDialog}
        setOpen={setLocalContactDialog}
        invite={selectedCommunity?.inviteFields || createEmptyCommunityInvite()}
        connection={connection}
      />
      <SetMemberInactiveModal
        open={setInactiveDialog}
        setOpen={setSetInactiveDialog}
        onConfirm={handleSetInactive}
        memberName={`${connection?.fields.FIRST_NAME} ${connection?.fields.LAST_NAME}`}
      />
    </>
  );
}
